import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HERO_VIDEO_URL = 'https://www.dropbox.com/scl/fi/x7dpfslbhphjtd75a7j27/03251-copy.mp4?rlkey=1d6gzusjv4wv7ffdkbtht4c7s&st=cim17mk1&raw=1';

function VideoModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl mx-4 lg:mx-8 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="whitespace-nowrap absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-white/20 text-white hover:bg-black/90 transition-colors cursor-pointer"
          aria-label="Close video"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {/* Caption */}
        <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-black/50 border border-white/15">
          <span className="font-body text-white/70 text-xs uppercase tracking-widest">
            Welcome from Jaquan Little · Creative Institute
          </span>
        </div>

        {/* Full video with controls + sound */}
        <video
          className="w-full aspect-video bg-black"
          autoPlay
          controls
          playsInline
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
      </div>

      {/* Hint to close */}
      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-body text-white/30 text-xs uppercase tracking-widest">
        Press ESC or click outside to close
      </p>
    </div>
  );
}

export default function HeroSection() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <section className="relative min-h-screen w-full flex items-center overflow-hidden">

        {/* ── Background Video (muted, autoplay, loop) ─── */}
        <video
          className="absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>

        {/* ── Brighter overlay — welcoming but readable ── */}
        <div className="absolute inset-0 z-10 bg-gradient-to-br from-black/50 via-[#1E3A8A]/40 to-[#2D1B69]/50" />
        <div className="absolute inset-x-0 bottom-0 h-56 z-10 bg-gradient-to-t from-black/45 to-transparent" />

        {/* ── Hero Content ─────────────────────────────── */}
        <div className="relative z-20 w-full max-w-screen-xl mx-auto px-4 sm:px-8 pt-28 sm:pt-36 pb-24 sm:pb-28 flex flex-col items-center text-center">

          {/* Heading — each word fades up in sequence */}
          <h1 className="font-heading text-white leading-tight mb-5 sm:mb-7 max-w-4xl">
            <span
              className="block text-base sm:text-2xl xl:text-4xl font-light text-white/65 tracking-[0.3em] uppercase mb-2"
              style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.1s' }}
            >
              The
            </span>
            <span
              className="block text-5xl sm:text-6xl xl:text-8xl font-black"
              style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.35s' }}
            >
              Creative
            </span>
            <span
              className="block text-5xl sm:text-6xl xl:text-8xl font-black text-brand-yellow"
              style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.6s' }}
            >
              Institute
            </span>
          </h1>

          {/* Subheading — split into phrases */}
          <p className="font-body text-white/90 text-sm sm:text-lg xl:text-xl max-w-2xl leading-relaxed mb-3 flex flex-wrap justify-center gap-x-2">
            {[
              'A premier music and creative development program —',
              'cultivating talent, confidence, and purpose',
              'through high-level instruction and mentorship.',
            ].map((phrase, i) => (
              <span
                key={i}
                className="block w-full"
                style={{
                  animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both',
                  animationDelay: `${0.85 + i * 0.22}s`,
                }}
              >
                {phrase}
              </span>
            ))}
          </p>

          <p
            className="hidden sm:block font-body text-white/60 text-sm xl:text-base max-w-xl leading-relaxed mb-8 sm:mb-10"
            style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '1.55s' }}
          >
            Specializing in piano, vocals, drums, creative coaching, and professional production services — fully mobile-based across multiple cities in North Carolina. We come to you.
          </p>

          {/* ── Evening & Group Highlights ───────────────── */}
          <div
            className="hidden sm:flex flex-wrap items-center justify-center gap-3 mb-8 sm:mb-10"
            style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '1.7s' }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 backdrop-blur-sm">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-moon-fill text-brand-yellow text-sm" />
              </span>
              <span className="font-body text-white text-xs font-semibold whitespace-nowrap">Night Classes · Open Until 9pm</span>
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-group-fill text-white/80 text-sm" />
              </span>
              <span className="font-body text-white text-xs font-semibold whitespace-nowrap">Group Sessions Available</span>
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-car-line text-white/80 text-sm" />
              </span>
              <span className="font-body text-white text-xs font-semibold whitespace-nowrap">Mobile-Based · We Come to You</span>
            </span>
          </div>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12 mt-4 sm:mt-0 w-full sm:w-auto"
            style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '1.9s' }}
          >
            <Link
              to="/enroll"
              className="whitespace-nowrap w-full sm:w-auto px-8 py-3.5 sm:py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm sm:text-base hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer"
            >
              Enroll Now
            </Link>
            <Link
              to="/on-site-programs"
              className="whitespace-nowrap w-full sm:w-auto px-8 py-3.5 sm:py-4 rounded-full border-2 border-white/50 text-white font-body font-semibold text-sm sm:text-base hover:border-brand-yellow hover:text-brand-yellow transition-all duration-300 cursor-pointer"
            >
              Explore Programs
            </Link>
            <Link
              to="/client-portal"
              className="whitespace-nowrap w-full sm:w-auto px-8 py-3.5 sm:py-4 rounded-full border-2 border-brand-purple-light/60 text-brand-purple-light font-body font-semibold text-sm sm:text-base hover:bg-brand-purple-light hover:text-white transition-all duration-300 cursor-pointer"
            >
              Work With Us
            </Link>
          </div>

          {/* ── Ministry Connections Hero Callout ── */}
          <div
            className="w-full max-w-2xl"
            style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '2.1s' }}
          >
            <Link
              to="/ministry-connections"
              className="group flex flex-col sm:flex-row items-center gap-4 sm:gap-6 px-6 py-5 rounded-2xl bg-black/40 border border-brand-yellow/30 backdrop-blur-md hover:bg-black/55 hover:border-brand-yellow/60 transition-all duration-300 cursor-pointer text-left"
            >
              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow/20 border border-brand-yellow/30 text-brand-yellow flex-shrink-0">
                <i className="ri-links-line text-2xl" />
              </div>

              {/* Text */}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                  <span className="font-body text-white font-bold text-sm">Ministry Staffing &amp; Creative Connections</span>
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-brand-yellow text-[#0F0E0C] font-body font-black text-[10px] uppercase tracking-wide">
                    Free
                  </span>
                </div>
                <p className="font-body text-white/50 text-xs leading-snug">
                  We connect creatives &amp; ministry professionals with churches and organizations — at no cost.
                </p>
              </div>

              {/* Arrow */}
              <div className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 text-white/40 group-hover:border-brand-yellow group-hover:text-brand-yellow transition-all duration-300 flex-shrink-0">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </Link>
          </div>


        </div>

        {/* ── Floating "Watch with Sound" button ───────── */}
        <button
          onClick={() => setModalOpen(true)}
          className="whitespace-nowrap absolute bottom-20 right-4 sm:right-10 z-20 flex items-center gap-2 sm:gap-3 group cursor-pointer"
          aria-label="Watch video with sound"
        >
          <span className="hidden sm:block font-body text-white/55 text-xs uppercase tracking-widest group-hover:text-white/90 transition-colors duration-300">
            Watch with Sound
          </span>
          <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-white/15 border border-white/30 text-white group-hover:bg-brand-yellow group-hover:border-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300">
            <i className="ri-play-fill text-xl sm:text-2xl ml-0.5 sm:ml-1" />
          </div>
        </button>

        {/* ── Scroll Indicator ─────────────────────────── */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 float-anim">
          <div className="flex flex-col items-center gap-2">
            <span className="font-body text-white/40 text-xs uppercase tracking-widest">Scroll</span>
            <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
          </div>
        </div>

        {/* ── Keyframes ────────────────────────────────── */}
        <style>{`
          @keyframes wordFadeUp {
            0%   { opacity: 0; transform: translateY(14px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>

      </section>

      {/* ── Video Lightbox Modal ──────────────────────── */}
      {modalOpen && <VideoModal onClose={() => setModalOpen(false)} />}
    </>
  );
}
